import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "../api";
import { useAuth } from "../context/AuthContext";
import DataTable from "../components/DataTable";
import { SupplierPaymentDialog } from "../components/SupplierPayment";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { fmtDate, fmtMoney } from "../utils/format";

export default function Suppliers() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [debtPicker, setDebtPicker] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", address: "", country: "", contact: "", email: "", company: "", bank_account: "", supplier_type: "Vendeur de véhicules" });
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canPay = ["admin", "comptable"].includes(user?.role);

  const delSupplier = async (s) => {
    if (!window.confirm(`Supprimer le fournisseur ${s.name} ?`)) return;
    try {
      await api.delete(`/suppliers/${s.id}`);
      toast.success("Fournisseur supprimé");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const load = () => api.get("/suppliers").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    const openId = params.get("open");
    if (openId) openDetail(openId);
  }, []);

  const openDetail = async (id) => {
    try {
      const r = await api.get(`/suppliers/${id}`);
      setDetail(r.data);
    } catch {}
  };

  const submit = async () => {
    if (!form.name) return toast.error("Nom requis");
    try {
      await api.post("/suppliers", form);
      toast.success("Fournisseur créé");
      setOpen(false);
      setForm({ name: "", phone: "", address: "", country: "", contact: "", email: "", company: "", bank_account: "", supplier_type: "Vendeur de véhicules" });
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="space-y-5" data-testid="suppliers-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Fournisseurs</h1>
          <p className="mt-1 text-sm text-slate-500">Historique des transactions et dettes fournisseurs.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-supplier-button"><Plus className="mr-1 h-4 w-4" /> Nouveau fournisseur</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nouveau fournisseur</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Nom *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="supplier-name-input" /></div>
              <div><Label>Entreprise</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
              <div><Label>Type de fournisseur</Label>
                <Select value={form.supplier_type} onValueChange={(v) => setForm({ ...form, supplier_type: v })}>
                  <SelectTrigger data-testid="supplier-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Vendeur de véhicules", "Transporteur", "Transitaire", "Garage", "Pièces détachées", "Assurance", "Autre"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>Pays</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="supplier-country-input" /></div>
              <div><Label>Contact</Label><Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div className="col-span-2"><Label>Compte bancaire / RIB</Label><Input value={form.bank_account} onChange={(e) => setForm({ ...form, bank_account: e.target.value })} /></div>
              <div className="col-span-2"><Label>Adresse</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            </div>
            <DialogFooter><Button onClick={submit} data-testid="supplier-submit">Créer</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <DataTable testidPrefix="suppliers-table" rows={rows}
        searchKeys={["name", "phone", "country"]}
        columns={[
          { key: "name", label: "Nom", render: (r) => <span className="font-semibold text-slate-800">{r.name}</span> },
          { key: "country", label: "Pays" },
          { key: "phone", label: "Téléphone" },
          { key: "vehicles_count", label: "Véhicules", className: "text-center" },
          { key: "total_bought", label: "Total acheté", className: "text-right", render: (r) => <span className="font-mono-num">{fmtMoney(r.total_bought)}</span> },
          { key: "total_paid", label: "Payé", className: "text-right", render: (r) => <span className="font-mono-num">{fmtMoney(r.total_paid)}</span> },
          { key: "debt", label: "Dette restante", className: "text-right", render: (r) => {
            const d = (r.total_bought || 0) - (r.total_paid || 0);
            return <span className={`font-mono-num font-semibold ${d > 0 ? "text-red-600" : "text-emerald-600"}`}>{fmtMoney(d)}</span>;
          } },
          { key: "actions", label: "", sortable: false, render: (r) => (
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => openDetail(r.id)} data-testid={`view-supplier-${r.id}`}><Eye className="h-4 w-4" /></Button>
              {isAdmin && <Button size="sm" variant="ghost" className="text-red-500" onClick={() => delSupplier(r)} data-testid={`delete-supplier-${r.id}`}><Trash2 className="h-4 w-4" /></Button>}
            </div>
          ) },
        ]}
      />

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader><DialogTitle>{detail.supplier.name}</DialogTitle></DialogHeader>
              <p className="text-sm text-slate-500">{detail.supplier.country || "—"} · {detail.supplier.phone || "—"} · {detail.supplier.contact || "—"}</p>
              <div className="mt-3 grid grid-cols-3 gap-3 text-center">
                <div className="rounded-lg bg-slate-50 p-3"><p className="text-xs text-slate-500">Acheté</p><p className="font-mono-num font-bold">{fmtMoney(detail.supplier.total_bought)}</p></div>
                <div className="rounded-lg bg-slate-50 p-3"><p className="text-xs text-slate-500">Payé</p><p className="font-mono-num font-bold text-emerald-600">{fmtMoney(detail.supplier.total_paid)}</p></div>
                <div className="rounded-lg bg-red-50 p-3"><p className="text-xs text-slate-500">Dette</p><p className="font-mono-num font-bold text-red-600">{fmtMoney(detail.supplier.debt)}</p></div>
              </div>
              {canPay && detail.purchases.some((p) => p.remaining > 0) && (
                <Button size="sm" className="mt-3" data-testid="supplier-pay-open-button" onClick={() => setDebtPicker(true)}>Effectuer un paiement</Button>
              )}
              <h4 className="font-heading mt-4 text-sm font-semibold">Véhicules achetés ({detail.vehicles.length})</h4>
              <div className="space-y-1">
                {detail.vehicles.map((v) => (
                  <button key={v.id} onClick={() => navigate(`/vehicules/${v.id}`)} className="flex w-full items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm hover:bg-blue-50">
                    <span className="font-medium">{v.brand} {v.model}</span>
                    <span className="font-mono-num text-xs text-slate-500">{v.internal_number}</span>
                    <span className="font-mono-num">{fmtMoney(v.purchase_price_fcfa)}</span>
                  </button>
                ))}
              </div>
              <h4 className="font-heading mt-4 text-sm font-semibold">Achats ({detail.purchases.length})</h4>
              <div className="space-y-1">
                {detail.purchases.map((p) => (
                  <div key={p.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
                    <span className="font-mono-num font-semibold">{p.purchase_number}</span>
                    <span className="text-xs text-slate-500">{fmtDate(p.purchase_date)}</span>
                    <span className="font-mono-num">{fmtMoney(p.price_fcfa)}</span>
                    <span className={`font-mono-num text-xs ${p.remaining > 0 ? "text-red-600" : "text-emerald-600"}`}>{p.remaining > 0 ? `Reste ${fmtMoney(p.remaining)}` : "Soldé"}</span>
                    {p.remaining > 0 && canPay && (
                      <Button size="sm" variant="outline" data-testid={`pay-supplier-purchase-${p.purchase_number}`}
                        onClick={() => setPayFor(p)}>Régler</Button>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={debtPicker} onOpenChange={setDebtPicker}>
        <DialogContent data-testid="supplier-debt-picker">
          <DialogHeader><DialogTitle>Effectuer un paiement — {detail?.supplier.name}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {detail?.purchases.filter((p) => p.remaining > 0).map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm">
                <div>
                  <p className="font-mono-num font-semibold">{p.purchase_number}</p>
                  <p className="text-xs text-slate-500">{p.vehicle_label}</p>
                </div>
                <span className="font-mono-num font-semibold text-red-600">{fmtMoney(p.remaining)}</span>
                <Button size="sm" data-testid={`picker-pay-${p.purchase_number}`} onClick={() => { setDebtPicker(false); setPayFor(p); }}>Payer</Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <SupplierPaymentDialog debt={payFor} onClose={() => setPayFor(null)}
        onPaid={() => { load(); if (detail) openDetail(detail.supplier.id); }} />
    </div>
  );
}
